/**
 * flexibleLineItems — treatment routing for custom statement line items.
 *
 * FIELDS-2. This is the TypeScript mirror of `core/flexible_line_items.py`,
 * and it exists for the same reason `financialMath.ts` mirrors
 * `core/financial_math.py`: the Normalization workspace shows derived figures
 * as the CPA types, before any round trip. If the browser routed a treatment
 * differently from the server, the number on screen would not be the number in
 * the report.
 *
 * TREATMENT IS THE WHOLE POINT. A custom line item with a label and an amount
 * is not information — "Misc. other income, 84,000" tells a calculator nothing.
 * The treatment is what says where the money goes, and an item without one is
 * counted NOWHERE (not zero, not revenue, not an add-back). That is deliberate:
 * an unclassified figure is an open question, and the honest answer is to leave
 * it out and keep asking.
 *
 * Keep this in lockstep with the Python module. Both are pinned to the same
 * golden vectors — `tests/test_flexible_line_items.py` and
 * `lib/flexibleLineItems.test.ts`, which mirror each other case-for-case.
 *
 * ATTEST-DEPLOY-UNIT-1 R2 prep 2/3 — TREATMENTS, Treatment, FlexibleItemSource
 * and FlexibleLineItem now live in `@/api/sharedTypes` so api/client.ts (the
 * one file every product's build includes) has no import specifier pointing
 * into this QoE-owned file. Re-exported below so every existing
 * `@/lib/flexibleLineItems` import — including the runtime `TREATMENTS`
 * value, not just the types — keeps working unchanged. Everything else in
 * this file (the routing logic, TREATMENT_MEANINGS, yearOf,
 * normalizeTreatment) stays exactly where it was.
 */

export { TREATMENTS } from '@/api/sharedTypes'
export type { FlexibleItemSource, FlexibleLineItem, Treatment } from '@/api/sharedTypes'
import { TREATMENTS } from '@/api/sharedTypes'
import type { FlexibleLineItem, Treatment } from '@/api/sharedTypes'

/**
 * One line per treatment, for the "+ Add line item" select. The server sends
 * the same map on GET /flexible-items so the two can never disagree; this copy
 * is the fallback for a form rendered before that payload has arrived.
 */
export const TREATMENT_MEANINGS: Record<Treatment, string> = {
  revenue: "Revenue — added to this year's revenue (raises gross profit and EBITDA)",
  opex: "Operating expense — added to this year's operating expenses (lowers EBITDA)",
  add_back: 'Add-back — a non-owner normalization (raises adjusted EBITDA)',
  owner_benefit: 'Owner benefit — counted in SDE only, never in adjusted EBITDA',
  debt_like: 'Debt-like item — reduces equity value in the bridge, no EBITDA effect',
  memo: 'Memo — recorded for the record, excluded from every calculation',
}

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim())

const num = (v: unknown): number => {
  const parsed = typeof v === 'number' ? v : Number(text(v))
  return Number.isFinite(parsed) ? parsed : 0
}

/** A fiscal year as an integer, or null when the value is blank or unusable. */
export function yearOf(value: unknown): number | null {
  const raw = text(value)
  if (!raw) return null
  const parsed = Number(raw)
  if (!Number.isFinite(parsed)) return null
  return Math.trunc(parsed)
}

/**
 * The canonical treatment, or null.
 *
 * Case- and whitespace-tolerant only. There is deliberately no aliasing:
 * 'addback' and 'add back' are NOT 'add_back'. Guessing at a near-miss is how
 * an item silently lands in the wrong bucket, and the wrong bucket is worse
 * than the review queue.
 */
export function normalizeTreatment(value: unknown): Treatment | null {
  const key = text(value).toLowerCase()
  return (TREATMENTS as readonly string[]).includes(key) ? (key as Treatment) : null
}

/** An adjustment row synthesized from a flexible item. */
export interface SyntheticAdjustment {
  category: string
  amount: number
  is_pending: false
  description: string
  year: number | null
  source: 'flexible_line_item'
  item_id?: string | null
}

export interface RoutedSummary {
  item_id?: string | null
  label: string
  amount: number
  year: number | null
  treatment: Treatment | null
  source: string
  note?: string | null
  raw_treatment?: string | null
  reason?: string
}

export interface DebtLikeEntry {
  category: string
  description: string
  amount: number
  note?: string | null
  item_id?: string | null
}

export interface RoutedItems {
  /** Added to the period's reported revenue before gross profit is computed. */
  revenueDelta: number
  /** Added to the period's reported operating expenses. */
  opexDelta: number
  /** Non-owner add-backs (raise adjusted EBITDA). */
  addBackAdjustments: SyntheticAdjustment[]
  /** Owner-benefit items (SDE only, per DECISION-1). */
  ownerBenefitAdjustments: SyntheticAdjustment[]
  /** Debt-like obligations for the EV -> equity bridge. No EBITDA effect. */
  debtLikeItems: DebtLikeEntry[]
  /** Recorded, excluded from every figure. */
  memoItems: RoutedSummary[]
  /** Counted nowhere; each entry carries a `reason`. The open review queue. */
  unrouted: RoutedSummary[]
  /** Both synthetic adjustment lists, in the shape financialMath reads. */
  adjustments: SyntheticAdjustment[]
}

function summary(item: FlexibleLineItem, treatment: Treatment | null): RoutedSummary {
  return {
    item_id: item.item_id ?? null,
    label: text(item.label),
    amount: num(item.amount),
    year: yearOf(item.year),
    treatment,
    source: text(item.source) || 'manual',
    note: item.note ?? null,
  }
}

/**
 * A flexible item rendered as an adjustment row.
 *
 * Routed through the existing adjustment shape rather than a new concept, so
 * the add-back / owner-benefit arithmetic stays the single implementation that
 * `financialMath` already holds under golden-vector test. `is_pending` is false
 * because an item WITH a treatment has been decided — the review gate for a
 * flexible item is the presence of a treatment, not a separate flag.
 */
function syntheticAdjustment(
  item: FlexibleLineItem,
  category: string,
): SyntheticAdjustment {
  return {
    category,
    amount: num(item.amount),
    is_pending: false,
    description: text(item.label) || 'Custom line item',
    year: yearOf(item.year),
    source: 'flexible_line_item',
    item_id: item.item_id ?? null,
  }
}

/**
 * Split flexible line items by treatment.
 *
 * `year` is the fiscal period being computed. Pass undefined/null for
 * deal-level consumers (the equity bridge, the memo register) to take every
 * item regardless of year. When a year IS given:
 *
 *   - items for another year are skipped silently — they belong to a different
 *     period, which is not a data problem;
 *   - an item with no usable year is skipped AND reported in `unrouted`,
 *     because it cannot be attributed to any period and would otherwise vanish
 *     without trace.
 */
export function routeFlexibleItems(
  items: FlexibleLineItem[] | null | undefined,
  year?: number | null,
): RoutedItems {
  const revenueAmounts: number[] = []
  const opexAmounts: number[] = []
  const addBacks: SyntheticAdjustment[] = []
  const ownerBenefits: SyntheticAdjustment[] = []
  const debtLike: DebtLikeEntry[] = []
  const memos: RoutedSummary[] = []
  const unrouted: RoutedSummary[] = []

  for (const item of items ?? []) {
    const rawTreatment = text(item.treatment)
    const treatment = normalizeTreatment(item.treatment)

    if (treatment === null) {
      unrouted.push({
        ...summary(item, null),
        raw_treatment: rawTreatment || null,
        reason: rawTreatment ? 'unknown_treatment' : 'missing_treatment',
      })
      continue
    }

    const itemYear = yearOf(item.year)
    if (year !== undefined && year !== null) {
      if (itemYear === null) {
        unrouted.push({ ...summary(item, treatment), reason: 'missing_year' })
        continue
      }
      if (itemYear !== year) continue
    }

    switch (treatment) {
      case 'revenue':
        revenueAmounts.push(num(item.amount))
        break
      case 'opex':
        opexAmounts.push(num(item.amount))
        break
      case 'add_back':
        addBacks.push(syntheticAdjustment(item, 'other'))
        break
      case 'owner_benefit':
        ownerBenefits.push(syntheticAdjustment(item, 'owner_comp'))
        break
      case 'debt_like':
        debtLike.push({
          category: 'Debt-Like Item',
          description: text(item.label) || 'Debt-like item',
          amount: num(item.amount),
          note: item.note ?? null,
          item_id: item.item_id ?? null,
        })
        break
      default:
        memos.push(summary(item, treatment))
        break
    }
  }

  const total = (values: number[]) => values.reduce((acc, v) => acc + v, 0)

  return {
    revenueDelta: total(revenueAmounts),
    opexDelta: total(opexAmounts),
    addBackAdjustments: addBacks,
    ownerBenefitAdjustments: ownerBenefits,
    debtLikeItems: debtLike,
    memoItems: memos,
    unrouted,
    adjustments: [...addBacks, ...ownerBenefits],
  }
}
